import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiArrowLeft, FiChevronRight, FiCheck, FiAlertTriangle } from "react-icons/fi";
import { apiGet, apiPost } from "../api";
import { PageHeader, Card, Pill, Btn, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";
import { MovementHistory } from "./Products";

/* ─── helpers ─── */
function stockStatus(p) {
  if (p.quantity === 0) return "out";
  if (p.quantity <= p.low_stock_threshold) return "low";
  return "ok";
}
const STATUS_LABEL = { ok: "In stock", low: "Low stock", out: "Out of stock" };

const REASONS = [
  { id: "count",  label: "Stock count",  mm: "ရေတွက်"    },
  { id: "damage", label: "Damaged",      mm: "ပျက်စီး"    },
  { id: "loss",   label: "Lost / theft", mm: "ပျောက်ဆုံး" },
  { id: "return", label: "Return",       mm: "ပြန်အမ်း"   },
];

/* ─── Step 1: product finder ─── */
function ProductFinder({ products, categories, onPick }) {
  const [q, setQ]     = useState("");
  const [cat, setCat] = useState("all");
  const [page, setPage] = useState(1);
  const qq = q.trim().toLowerCase();
  const matches = products.filter(p =>
    (cat === "all" || String(p.category) === cat) &&
    (!qq || p.name.toLowerCase().includes(qq) || (p.sku || "").toLowerCase().includes(qq))
  );
  const list = matches.slice((page - 1) * PER_PAGE_DEFAULT, page * PER_PAGE_DEFAULT);

  return (
    <div>
      <PageHeader
        title="Stock Adjustment"
        mm="ပြင်ဆင်"
        desc={`${products.length} product${products.length !== 1 ? "s" : ""} · ${categories.length} categor${categories.length !== 1 ? "ies" : "y"} · find the one you need to correct`}
      />
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        {/* search */}
        <div style={{ display: "flex", alignItems: "center", gap: 11, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-md)", padding: "14px 16px", boxShadow: "var(--shadow-sm)" }}>
          <FiSearch size={20} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input autoFocus value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by name or SKU…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 15.5, color: "var(--ink)", width: "100%" }} />
        </div>

        {/* category filter chips */}
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {[{ id: "all", name: "All" }, ...categories].map(c => {
            const on = cat === String(c.id);
            return (
              <button key={c.id} onClick={() => { setCat(String(c.id)); setPage(1); }}
                style={{ padding: "7px 14px", borderRadius: 999, cursor: "pointer", border: `1px solid ${on ? "var(--primary)" : "var(--line)"}`, background: on ? "var(--primary)" : "var(--surface)", color: on ? "#fff" : "var(--n-600)", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600 }}>
                {c.name}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {list.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--n-400)", fontSize: 14, padding: "28px 0" }}>
              {qq ? `No products match "${q}".` : "No products in this category."}
            </div>
          )}
          {list.map(p => {
            const st = stockStatus(p);
            return (
              <button key={p.id} onClick={() => onPick(p)}
                style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", textAlign: "left", padding: "13px 16px", borderRadius: "var(--r-md)", border: "1px solid var(--line)", background: "var(--surface)", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--blue-50)"; e.currentTarget.style.borderColor = "var(--blue-100)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.borderColor = "var(--line)"; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{p.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 2 }}>{p.sku} · {p.category_name || "—"}</div>
                </div>
                <div style={{ fontSize: 13, color: "var(--sub)", fontVariantNumeric: "tabular-nums" }}>{p.quantity} pcs</div>
                <Pill tone={st}>{STATUS_LABEL[st]}</Pill>
                <FiChevronRight size={18} color="var(--n-300)" />
              </button>
            );
          })}
        </div>

        <Pagination total={matches.length} page={page} onChange={setPage} />
      </div>
    </div>
  );
}

/* ─── Step 2: adjust form ─── */
function AdjustForm({ product, token, go, location, onBack, onApplied }) {
  const [reason, setReason]     = useState("count");
  const [newCount, setNewCount] = useState(String(product.quantity));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");

  const current  = product.quantity;
  const newVal   = Math.max(0, parseInt(newCount) || 0);
  const delta    = newVal - current;
  const canApply = newVal !== current;

  const handleApply = async () => {
    if (!canApply) return;
    if (!location?.id) return setError("No working location selected — choose one at the top of the screen.");
    setSubmitting(true); setError("");
    try {
      const res = await apiPost("/api/inventory/products/adjust", {
        note: REASONS.find(r => r.id === reason)?.label || reason,
        location_id: location.id,
        products: [{ product_id: product.id, quantity: delta }],
      }, token);
      if (res.ok) {
        onApplied({ ...product, quantity: newVal });
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to apply adjustment.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSubmitting(false); }
  };

  const st = stockStatus(product);

  return (
    <div>
      {/* back + title */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 25, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)" }}>Adjust stock</h1>
          <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 3 }}>{product.name} · {product.sku || "—"}</div>
        </div>
        <Pill tone={st} style={{ marginLeft: "auto" }}>{STATUS_LABEL[st]}</Pill>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 16, alignItems: "start" }}>
        {/* form */}
        <Card style={{ padding: 24 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 16 }}>
            New adjustment
          </div>

          {/* reason buttons */}
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", marginBottom: 8 }}>Reason</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
            {REASONS.map(r => (
              <button key={r.id} onClick={() => setReason(r.id)}
                style={{ padding: "11px 12px", borderRadius: "var(--r-sm)", cursor: "pointer", textAlign: "left", border: `1px solid ${reason === r.id ? "var(--primary)" : "var(--line)"}`, background: reason === r.id ? "var(--blue-50)" : "var(--surface)" }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: reason === r.id ? "var(--primary)" : "var(--ink)" }}>{r.label}</div>
                <div style={{ fontFamily: "var(--font-mm)", fontSize: 11.5, color: "var(--n-400)", marginTop: 2 }}>{r.mm}</div>
              </button>
            ))}
          </div>

          {/* counts */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <CountField label="Current" unit="pcs">
              <input value={current} readOnly
                style={{ ...inputSt, color: "var(--sub)" }} />
            </CountField>
            <CountField label="New count" unit="pcs">
              <input
                type="number" min="0"
                value={newCount}
                onChange={e => setNewCount(e.target.value.replace(/[^\d]/g, ""))}
                style={inputSt}
              />
            </CountField>
          </div>

          {/* delta preview */}
          {canApply && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", marginBottom: 14, borderRadius: "var(--r-sm)", background: delta > 0 ? "var(--success-bg)" : "var(--danger-bg)" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: delta > 0 ? "var(--success)" : "var(--danger)" }}>
                {delta > 0 ? `+${delta}` : delta} pcs — stock will go from {current} → {newVal}
              </span>
            </div>
          )}

          {error && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, color: "var(--danger)", fontSize: 12.5, fontWeight: 600 }}>
              <FiAlertTriangle size={14} /> {error}
            </div>
          )}

          <Btn kind="pri" onClick={handleApply} disabled={!canApply || submitting}
            style={{ width: "100%", justifyContent: "center", height: 48 }}>
            <FiCheck size={16} /> {submitting ? "Applying…" : "Apply Adjustment"}
          </Btn>
        </Card>

        {/* movement history */}
        <MovementHistory productId={product.id} token={token} />
      </div>
    </div>
  );
}

function CountField({ label, unit, children }) {
  return (
    <div>
      <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", display: "block", marginBottom: 7 }}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "11px 13px", background: "var(--surface)" }}>
        {children}
        <span style={{ fontSize: 13, color: "var(--n-400)", fontWeight: 600 }}>{unit}</span>
      </div>
    </div>
  );
}

const inputSt = {
  border: "none", outline: "none", background: "transparent",
  width: "100%", fontFamily: "var(--font-body)", fontSize: 15, fontWeight: 700,
  color: "var(--ink)", fontVariantNumeric: "tabular-nums",
};

/* ─── Root ─── */
export default function Adjust({ token, go, location }) {
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null); // null = finder view

  const fetchProducts = useCallback(async () => {
    try {
      const qs = location?.id ? `?location_id=${location.id}` : "";
      const [pr, cr] = await Promise.all([
        apiGet(`/api/inventory/products/by-location${qs}`, token).then(r => r.json()),
        apiGet("/api/inventory/categories", token).then(r => r.json()),
      ]);
      setProducts(Array.isArray(pr) ? pr : []);
      setCategories(Array.isArray(cr) ? cr : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token, location?.id]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleApplied = (updatedProduct) => {
    setProducts(ps => ps.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    setSelected(updatedProduct);
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  if (selected) {
    return (
      <AdjustForm
        product={selected}
        token={token}
        go={go}
        location={location}
        onBack={() => setSelected(null)}
        onApplied={handleApplied}
      />
    );
  }

  return <ProductFinder products={products} categories={categories} onPick={setSelected} />;
}
